import { useState } from 'react';
import { config } from '@/config/env';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type FeedbackType = 'bug' | 'suggestion' | 'other';

const FEEDBACK_TYPES: { value: FeedbackType; label: string; emoji: string }[] = [
  { value: 'bug', label: '버그 신고', emoji: '🐛' },
  { value: 'suggestion', label: '기능 제안', emoji: '💡' },
  { value: 'other', label: '기타 의견', emoji: '📝' },
];

export default function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const [type, setType] = useState<FeedbackType>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim() || !description.trim()) {
      setError('제목과 내용을 입력해주세요.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${config.apiUrl}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type,
          title: title.trim(),
          description: description.trim(),
          email: email.trim() || undefined,
          deviceInfo: navigator.userAgent,
          url: window.location.href,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || '제출에 실패했습니다.');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // 닫을 때 폼 초기화
    setType('bug');
    setTitle('');
    setDescription('');
    setEmail('');
    setSubmitted(false);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-slate-800 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold">피드백 보내기</h2>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-white text-xl"
          >
            ✕
          </button>
        </div>

        {submitted ? (
          /* 제출 완료 */
          <div className="p-6 text-center">
            <div className="text-5xl mb-4">✅</div>
            <h3 className="text-xl font-semibold mb-2">감사합니다!</h3>
            <p className="text-slate-400 mb-6">
              소중한 피드백이 전달되었습니다.<br />
              더 나은 서비스를 위해 노력하겠습니다.
            </p>
            <button
              onClick={handleClose}
              className="btn-primary px-6 py-2 rounded-lg"
            >
              닫기
            </button>
          </div>
        ) : (
          /* 피드백 폼 */
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* 유형 선택 */}
            <div>
              <label className="block text-sm text-slate-400 mb-2">유형</label>
              <div className="flex gap-2">
                {FEEDBACK_TYPES.map((ft) => (
                  <button
                    key={ft.value}
                    type="button"
                    onClick={() => setType(ft.value)}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm transition-colors ${
                      type === ft.value
                        ? 'bg-primary-600 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {ft.emoji} {ft.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 제목 */}
            <div>
              <label className="block text-sm text-slate-400 mb-2">
                제목 <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={
                  type === 'bug'
                    ? '예: 게임 중 소리가 안 들려요'
                    : type === 'suggestion'
                    ? '예: 이런 기능이 있으면 좋겠어요'
                    : '제목을 입력해주세요'
                }
                maxLength={100}
                className="w-full px-4 py-3 bg-slate-700 rounded-lg border border-slate-600 focus:border-primary-500 focus:outline-none"
              />
            </div>

            {/* 내용 */}
            <div>
              <label className="block text-sm text-slate-400 mb-2">
                내용 <span className="text-red-400">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={
                  type === 'bug'
                    ? '어떤 상황에서 문제가 발생했는지 자세히 알려주세요.\n\n예:\n- 어떤 화면에서 발생했나요?\n- 어떤 동작을 했나요?\n- 예상한 결과와 실제 결과는?'
                    : '의견을 자유롭게 작성해주세요.'
                }
                rows={5}
                maxLength={2000}
                className="w-full px-4 py-3 bg-slate-700 rounded-lg border border-slate-600 focus:border-primary-500 focus:outline-none resize-none"
              />
              <p className="text-xs text-slate-500 mt-1 text-right">
                {description.length}/2000
              </p>
            </div>

            {/* 이메일 (선택) */}
            <div>
              <label className="block text-sm text-slate-400 mb-2">
                이메일 <span className="text-slate-500">(선택)</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="답변 받으실 이메일"
                className="w-full px-4 py-3 bg-slate-700 rounded-lg border border-slate-600 focus:border-primary-500 focus:outline-none"
              />
              <p className="text-xs text-slate-500 mt-1">
                답변이 필요하시면 이메일을 남겨주세요
              </p>
            </div>

            {/* 에러 메시지 */}
            {error && (
              <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* 제출 버튼 */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-slate-600 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
            >
              {isSubmitting ? '제출 중...' : '피드백 보내기'}
            </button>

            <p className="text-xs text-slate-500 text-center">
              제출된 피드백은 서비스 개선에 활용됩니다
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
